import type { BehaviourFailurePolicy } from "app/services/behaviourRulesApi";

export const MISSION_STEP_FAILURE_TOPIC = "__mission_step_failure__";

export const MISSION_STEP_FAILURE_ACTION_TYPES = [
    "NavigateTo",
    "WalkStair",
    "TakePhoto",
    "Relocalization",
    "Record360",
    "Docking",
    "PressElevatorButton",
    "ArmPreset",
] as const;

export type MissionStepFailureActionType = (typeof MISSION_STEP_FAILURE_ACTION_TYPES)[number];

export const FAILURE_POLICY_ACTION_TYPES = {
    CONTINUE: "failure_continue",
    STOP: "failure_stop",
    RTL: "failure_rtl",
    RETRY: "failure_retry",
    PLAN_NEXT: "failure_plan_next",
    RELOCALIZE_PRIOR: "failure_relocalize_prior",
} as const;

export const RETRY_ELIGIBLE_FAILURE_ACTION_TYPES = [
    "TakePhoto",
    "Relocalization",
    "Record360",
    "PressElevatorButton",
] as const;

export const isMissionStepFailureSource = (source: { kind?: string } | undefined) =>
    source?.kind === "mission_step_failure";

export const isMissionStepFailureCondition = (condition: { topic?: string } | undefined) =>
    condition?.topic === MISSION_STEP_FAILURE_TOPIC;

export const isFailurePolicyRule = (rule: {
    id?: string;
    isFailurePolicy?: boolean;
    conditions?: { items?: Array<{ topic?: string; field?: string }> };
} | undefined) =>
    Boolean(
        rule?.isFailurePolicy ||
            String(rule?.id || "").startsWith("failure-policy:") ||
            isMissionStepFailureCondition(rule?.conditions?.items?.[0]),
    );

export const failureActionTypeFromRule = (
    rule: {
        id?: string;
        failureActionType?: string;
        conditions?: { items?: Array<{ topic?: string; field?: string }> };
    },
    eventSources: Array<{ topic?: string; field?: string; action_type?: string }> = [],
) => {
    if (rule.failureActionType) return rule.failureActionType as MissionStepFailureActionType;
    const condition = rule.conditions?.items?.[0];
    const source = eventSources.find(
        (item) => item.topic === condition?.topic && item.field === condition?.field,
    );
    const fromSource = failureActionTypeFromSource(source);
    if (fromSource) return fromSource;
    const fromId = String(rule.id || "").split(":")[1];
    if (
        fromId &&
        MISSION_STEP_FAILURE_ACTION_TYPES.includes(fromId as MissionStepFailureActionType)
    ) {
        return fromId as MissionStepFailureActionType;
    }
    return failureActionTypeFromSource({ field: condition?.field });
};

export const failureActionTypeFromSource = (
    source: { action_type?: string; field?: string } | undefined,
): MissionStepFailureActionType | null => {
    const actionType = source?.action_type || source?.field;
    if (
        actionType &&
        MISSION_STEP_FAILURE_ACTION_TYPES.includes(
            actionType as MissionStepFailureActionType,
        )
    ) {
        return actionType as MissionStepFailureActionType;
    }
    return null;
};

export const failurePolicyFromRuleAction = (
    action: {
        type?: string;
        params?: Record<string, string>;
    },
    options: { enabled?: boolean } = {},
): BehaviourFailurePolicy => {
    const withEnabled = (policy: BehaviourFailurePolicy): BehaviourFailurePolicy =>
        options.enabled === false ? { ...policy, enabled: false } : policy;

    if (action.type === FAILURE_POLICY_ACTION_TYPES.CONTINUE) {
        return withEnabled({ on_failure: "continue" });
    }
    if (action.type === FAILURE_POLICY_ACTION_TYPES.PLAN_NEXT) {
        const rawExhausted = action.params?.on_exhausted;
        const onPlanNextExhausted =
            rawExhausted === "stop" || rawExhausted === "continue" ? rawExhausted : "rtl";
        const skipFollowOn =
            action.params?.skip_follow_on_actions === "false" ? "false" : "true";
        const policy: BehaviourFailurePolicy = {
            on_failure: "plan_next",
            plan_next_max: action.params?.plan_next_max || "3",
            on_plan_next_exhausted: onPlanNextExhausted,
            skip_follow_on_actions: skipFollowOn,
        };
        if (onPlanNextExhausted === "rtl") {
            policy.rtl_mode = action.params?.mode || "plan_to_launch";
        }
        return withEnabled(policy);
    }
    if (action.type === FAILURE_POLICY_ACTION_TYPES.RELOCALIZE_PRIOR) {
        return withEnabled({ on_failure: "relocalize_prior" });
    }
    if (action.type === FAILURE_POLICY_ACTION_TYPES.RTL) {
        return withEnabled({
            on_failure: "rtl",
            rtl_mode: action.params?.mode || "plan_to_launch",
        });
    }
    if (action.type === FAILURE_POLICY_ACTION_TYPES.RETRY) {
        const rawExhausted = action.params?.on_exhausted;
        const onRetryExhausted =
            rawExhausted === "rtl" || rawExhausted === "continue" ? rawExhausted : "stop";
        const policy: BehaviourFailurePolicy = {
            on_failure: "retry",
            retry_count: action.params?.retry_count || "1",
            on_retry_exhausted: onRetryExhausted,
        };
        if (onRetryExhausted === "rtl") {
            policy.rtl_mode = action.params?.mode || "plan_to_launch";
        }
        return withEnabled(policy);
    }
    return withEnabled({ on_failure: "stop" });
};

export const ruleActionFromFailurePolicy = (policy: BehaviourFailurePolicy) => {
    if (policy.on_failure === "continue") {
        return { type: FAILURE_POLICY_ACTION_TYPES.CONTINUE, params: {} };
    }
    if (policy.on_failure === "plan_next") {
        const onExhausted = policy.on_plan_next_exhausted || "rtl";
        return {
            type: FAILURE_POLICY_ACTION_TYPES.PLAN_NEXT,
            params: {
                plan_next_max: policy.plan_next_max || "3",
                on_exhausted: onExhausted,
                mode: policy.rtl_mode || "plan_to_launch",
                skip_follow_on_actions:
                    policy.skip_follow_on_actions === "false" ? "false" : "true",
            },
        };
    }
    if (policy.on_failure === "relocalize_prior") {
        return { type: FAILURE_POLICY_ACTION_TYPES.RELOCALIZE_PRIOR, params: {} };
    }
    if (policy.on_failure === "rtl") {
        return {
            type: FAILURE_POLICY_ACTION_TYPES.RTL,
            params: { mode: policy.rtl_mode || "plan_to_launch" },
        };
    }
    if (policy.on_failure === "retry") {
        const onExhausted = policy.on_retry_exhausted || "stop";
        return {
            type: FAILURE_POLICY_ACTION_TYPES.RETRY,
            params: {
                retry_count: policy.retry_count || "1",
                on_exhausted: onExhausted,
                mode: policy.rtl_mode || "plan_to_launch",
            },
        };
    }
    return { type: FAILURE_POLICY_ACTION_TYPES.STOP, params: {} };
};

export const failurePolicyToRule = (
    actionType: MissionStepFailureActionType,
    label: string,
    policy: BehaviourFailurePolicy,
) => ({
    id: `failure-policy:${actionType}`,
    name: `${label} failure`,
    enabled: policy.enabled !== false,
    isFailurePolicy: true,
    failureActionType: actionType,
    conditions: {
        logic: "and",
        items: [
            {
                source: "mission_step_failure",
                topic: MISSION_STEP_FAILURE_TOPIC,
                field: actionType,
                operator: "eq",
                value: "true",
            },
        ],
    },
    action: ruleActionFromFailurePolicy(policy),
});

export const buildFailurePolicyRules = (
    failurePolicies: Record<string, BehaviourFailurePolicy> | undefined,
    catalogActions: Array<{ action_type: string; label: string }> | undefined,
) => {
    const labels = new Map(
        (catalogActions ?? []).map((item) => [item.action_type, item.label]),
    );
    return MISSION_STEP_FAILURE_ACTION_TYPES.filter((actionType) => {
        const policy = failurePolicies?.[actionType];
        return Boolean(policy);
    }).map((actionType) =>
        failurePolicyToRule(
            actionType,
            labels.get(actionType) ?? actionType,
            failurePolicies?.[actionType] as BehaviourFailurePolicy,
        ),
    );
};
